import { useEffect, useMemo, useRef, useState } from "react";
import {
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  XCircle,
  RefreshCw,
  Repeat,
  Hash,
  Plus as PlusIcon,
  X as XIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Alert,
  AlertDescription,
  Button,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ToggleGroup,
  ToggleGroupItem,
  Skeleton,
  Textarea,
} from "@sportscanner/ui";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/responsive-dialog";
import {
  adminCreateBookingSchema,
  bulkBookingSchema,
  addPrice,
} from "@sportscanner/schemas";
import { apiGet, apiPost, getApiErrorCode } from "@/lib/api";
import {
  generateDates,
  dateStrToEntry,
  durationLabel,
  formatRSD,
  formatCourtLabel,
  type DateEntry,
} from "./helpers";
import DateSlider from "./date-slider";
import PhoneInput from "./phone-input";
import RecurrenceField, {
  defaultRecurrence,
  type RecurrenceValue,
} from "./recurrence-field";

const TOGGLE_CLS =
  "w-full py-2.5 rounded-full text-sm font-semibold border border-neutral-200 cursor-pointer " +
  "data-[state=off]:bg-white data-[state=off]:text-neutral-600 " +
  "data-[state=on]:bg-primary-500 data-[state=on]:text-white data-[state=on]:border-primary-500 " +
  "hover:bg-primary-50 hover:border-primary-500 transition-colors " +
  "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-neutral-200";

// Admins aren't bound by the player-facing bookingDaysAdvance window. The date
// slider shows a fixed 30-day window; calendar-cell shortcuts fetch their own
// specific date instead, so they work no matter how far ahead it is.
const ADMIN_BOOKING_WINDOW_DAYS = 30;

// Field-validation errors that should keep the user on the summary step
// with an inline alert above the form, instead of routing to ErrorStep.
const FIELD_ERROR_CODES = new Set([
  "PHONE_INVALID",
  "EMAIL_INVALID",
  "PHONE_REQUIRED",
  "EMAIL_REQUIRED",
]);

// Coach validation failures stay on the summary step as an inline alert (rather
// than routing to ErrorStep or the slot-conflict screen), so the admin can pick
// a different coach or slot without losing the form.
const COACH_ERROR_CODES = new Set([
  "COACHING_DISABLED",
  "COACH_NOT_FOUND",
  "COACH_INACTIVE",
  "COACH_SPORT_INVALID",
  "COACH_UNAVAILABLE",
  "COACH_TAKEN",
]);

interface Sport {
  id: string;
  name: string;
  sessionDurationMinutes: number;
}

// One row in the additional-players roster. `playerUserId` is set only when
// the admin picked a registered member from the search dropdown (requireLogin
// clubs); free-text rows keep it null and persist as a plain string. Mirrors
// the same type on the public web booking form.
interface RosterEntry {
  name: string;
  playerUserId: string | null;
}

interface MemberSearchResult {
  id: string;
  name: string;
  email: string;
}

type QuotaViolationCode =
  | "DAILY_LIMIT_EXCEEDED"
  | "CREATION_DAY_LIMIT_EXCEEDED"
  | "CONSECUTIVE_DAY_NOT_ALLOWED"
  | "PRIME_TIME_QUOTA_EXCEEDED";

interface QuotaViolationItem {
  code: QuotaViolationCode;
  limit?: number;
  conflictDate?: string;
  weekStart?: string;
  weekEnd?: string;
  // Set when the violation was triggered by a linked roster opponent rather
  // than the booker — the override prompt prefixes the violation with this
  // name so the admin knows which player is at their cap.
  playerName?: string;
}
interface Court {
  id: string;
  name: string;
  sportId: string;
  isIndoor: boolean;
  pricePerSession: string;
}
interface AvailabilitySport {
  sportId: string;
  sportName: string;
  sessionDurationMinutes: number;
  courtsHaveSameSchedule: boolean;
  dates: Record<string, Record<string, string[]>>;
}
interface CoachOption {
  id: string;
  name: string;
  sports: { sportId: string; pricePerSession: string }[];
}
interface AvailabilityData {
  clubId: string;
  features: {
    collectPhone: boolean;
    requirePhone: boolean;
    collectEmail: boolean;
    requireEmail: boolean;
    requirePlayerRoster: boolean;
    sendBookingConfirmationSms: boolean;
    requireLogin: boolean;
    allowCoaching: boolean;
  };
  sports: Sport[];
  courts: Court[];
  // Active coaches (only when coaching is enabled) — the summary step offers an
  // optional coach for the selected sport.
  coaches?: CoachOption[];
  availability: AvailabilitySport[];
}

interface BookingResponse {
  id: string;
  bookingRef: string;
  courtName: string;
  sportName: string;
  clubName: string;
  date: string;
  startTime: string;
  endTime: string;
  playerName: string;
  playerPhone: string | null;
  price: string;
  duration: string;
  status: string;
}

interface Alternative {
  time: string;
  courtId: string;
  courtName: string;
}

type BulkConflictReason =
  | "SLOT_TAKEN"
  | "SLOT_BLOCKED"
  | "BOOKING_IN_PAST"
  | "CLUB_CLOSED";

interface BulkBookingResponse {
  seriesId: string;
  courtId: string;
  courtName: string;
  sportId: string;
  sportName: string;
  sessionDurationMinutes: number;
  pricePerSession: string;
  playerName: string;
  playerPhone: string | null;
  playerEmail: string | null;
  created: Array<{
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    accessToken: string;
    reservationUrl: string;
  }>;
  conflicts: Array<{
    date: string;
    startTime: string;
    reason: BulkConflictReason;
  }>;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clubId: string;
  // Free clubs don't charge — hide every price label/row in the flow.
  isFree: boolean;
  /**
   * Shortcut prefill: opening from a calendar cell. The dialog starts at the
   * court-pick step with date+time fixed. Otherwise the dialog starts at the
   * full picker.
   */
  shortcut?: { date: string; time: string; sportId?: string };
  onCreated?: () => void;
}

type Step =
  | "select-court"
  | "select"
  | "summary"
  | "success"
  | "conflict"
  | "bulk-result"
  | "error";

export default function BookingDialog({
  open,
  onOpenChange,
  clubId,
  isFree,
  shortcut,
  onCreated,
}: Props) {
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>(shortcut ? "select-court" : "select");
  const [selectedSportId, setSelectedSportId] = useState<string>(
    shortcut?.sportId ?? "",
  );
  const [selectedDate, setSelectedDate] = useState<string>(
    shortcut?.date ?? "",
  );
  const [selectedTime, setSelectedTime] = useState<string>(
    shortcut?.time ?? "",
  );
  const [selectedCourtId, setSelectedCourtId] = useState<string>("");
  // Optional coach for a coached session (front desk booking on behalf of a
  // caller). "" = no coach.
  const [selectedCoachId, setSelectedCoachId] = useState<string>("");

  const [playerName, setPlayerName] = useState("");
  const [playerPhone, setPlayerPhone] = useState("");
  const [playerEmail, setPlayerEmail] = useState("");
  const [comment, setComment] = useState("");
  // Roster rows carry an optional playerUserId for the member-search picker;
  // null = free-text. See web/booking-summary for the same shape.
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [rosterFormat, setRosterFormat] = useState<"singles" | "doubles">(
    "singles",
  );
  // Quota violations from the last submit attempt. When set, an inline modal
  // asks the user to confirm an override before resubmitting.
  const [quotaPrompt, setQuotaPrompt] = useState<{
    violations: QuotaViolationItem[];
    canOverride: boolean;
  } | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState<BookingResponse | null>(
    null,
  );
  const [bulkResult, setBulkResult] = useState<BulkBookingResponse | null>(
    null,
  );
  const [alternatives, setAlternatives] = useState<Alternative[]>([]);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [formAlertCode, setFormAlertCode] = useState<string | null>(null);

  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrence, setRecurrence] = useState<RecurrenceValue>(() =>
    defaultRecurrence(shortcut?.date ?? ""),
  );

  const availQuery = useQuery({
    queryKey: ["admin", "availability", clubId, shortcut?.date ?? "window"],
    queryFn: () => {
      const params = new URLSearchParams();
      if (shortcut) {
        // Calendar cell: only this exact date is needed, and it may be well
        // past the 30-day slider window — fetch it directly.
        params.set("startDate", shortcut.date);
        params.set("days", "1");
      } else {
        params.set("days", String(ADMIN_BOOKING_WINDOW_DAYS));
      }
      return apiGet<AvailabilityData>(
        `/api/admin/clubs/${clubId}/availability?${params.toString()}`,
      );
    },
    enabled: open,
    staleTime: 0,
  });

  // Reset state when the dialog opens.
  useEffect(() => {
    if (!open) return;
    setStep(shortcut ? "select-court" : "select");
    setSelectedSportId(shortcut?.sportId ?? "");
    setSelectedDate(shortcut?.date ?? "");
    setSelectedTime(shortcut?.time ?? "");
    setSelectedCourtId("");
    setSelectedCoachId("");
    setPlayerName("");
    setPlayerPhone("");
    setPlayerEmail("");
    setComment("");
    setBookingResult(null);
    setBulkResult(null);
    setAlternatives([]);
    setErrorCode(null);
    setFormAlertCode(null);
    setIsSubmitting(false);
    setIsRecurring(false);
    setRoster([]);
    setQuotaPrompt(null);
    setRecurrence(defaultRecurrence(shortcut?.date ?? ""));
    void availQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep recurrence defaults aligned with the chosen first occurrence: when
  // the user picks a different date in the select step, the default weekday
  // should follow until the user has explicitly enabled recurrence.
  useEffect(() => {
    if (isRecurring) return;
    if (!selectedDate) return;
    setRecurrence((r) => {
      const fresh = defaultRecurrence(selectedDate);
      return { ...r, weekdays: fresh.weekdays };
    });
  }, [selectedDate, isRecurring]);

  // Auto-select sport/date when availability arrives (full mode).
  useEffect(() => {
    if (!availQuery.data || shortcut) return;
    if (!selectedSportId && availQuery.data.sports.length > 0) {
      setSelectedSportId(availQuery.data.sports[0].id);
    }
    if (!selectedDate) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      setSelectedDate(todayStr);
    }
  }, [availQuery.data, shortcut, selectedSportId, selectedDate]);

  const dates = useMemo<DateEntry[]>(() => {
    if (!availQuery.data) return [];
    return generateDates(ADMIN_BOOKING_WINDOW_DAYS);
  }, [availQuery.data]);

  // Determine sport for shortcut mode based on the courts available at the slot.
  const shortcutSports = useMemo(() => {
    if (!shortcut || !availQuery.data) return [];
    return availQuery.data.availability.filter((s) =>
      Object.values(s.dates[shortcut.date] ?? {}).some((slots) =>
        slots.includes(shortcut.time),
      ),
    );
  }, [shortcut, availQuery.data]);

  // For shortcut: auto-pick the first sport with availability so the dialog
  // never opens with no sport selected when at least one is available.
  useEffect(() => {
    if (!shortcut || !availQuery.data) return;
    if (selectedSportId) return;
    if (shortcutSports.length > 0) {
      setSelectedSportId(shortcutSports[0].sportId);
    }
  }, [shortcut, availQuery.data, selectedSportId, shortcutSports]);

  const currentSportAvail = useMemo<AvailabilitySport | undefined>(() => {
    return availQuery.data?.availability.find(
      (s) => s.sportId === selectedSportId,
    );
  }, [availQuery.data, selectedSportId]);

  // When courts have staggered start times, time slots differ per court, so
  // the court must be picked first and time slots scoped to it.
  const isSameSchedule = currentSportAvail?.courtsHaveSameSchedule !== false;

  const dayAvailability = useMemo(() => {
    if (!currentSportAvail || !selectedDate) return null;
    return currentSportAvail.dates[selectedDate] ?? null;
  }, [currentSportAvail, selectedDate]);

  const sportCourts = useMemo<Court[]>(() => {
    if (!availQuery.data || !selectedSportId) return [];
    const list = availQuery.data.courts.filter(
      (c) => c.sportId === selectedSportId,
    );
    return [...list].sort((a, b) => {
      const an = Number(a.name);
      const bn = Number(b.name);
      if (!isNaN(an) && !isNaN(bn)) return an - bn;
      return a.name.localeCompare(b.name);
    });
  }, [availQuery.data, selectedSportId]);

  // Available courts for shortcut (date+time fixed).
  const shortcutCourts = useMemo<Court[]>(() => {
    if (!shortcut || !availQuery.data || !dayAvailability) return [];
    return sportCourts.filter((c) =>
      (dayAvailability[c.id] ?? []).includes(shortcut.time),
    );
  }, [shortcut, availQuery.data, dayAvailability, sportCourts]);

  // Time slots for full mode at the selected sport+date. Same-schedule clubs
  // pool slots across all courts; staggered clubs scope them to the picked court.
  const visibleTimeSlots = useMemo<string[]>(() => {
    if (!dayAvailability) return [];
    if (!isSameSchedule) {
      return selectedCourtId
        ? [...(dayAvailability[selectedCourtId] ?? [])].sort()
        : [];
    }
    const all = new Set<string>();
    for (const c of sportCourts) {
      for (const s of dayAvailability[c.id] ?? []) all.add(s);
    }
    return Array.from(all).sort();
  }, [dayAvailability, sportCourts, isSameSchedule, selectedCourtId]);

  // Whether the selected sport+date has any bookable slot on any court.
  const hasAnySlot = useMemo(() => {
    if (!dayAvailability) return false;
    return sportCourts.some((c) => (dayAvailability[c.id]?.length ?? 0) > 0);
  }, [dayAvailability, sportCourts]);

  function isCourtDisabledForTime(courtId: string): boolean {
    if (!dayAvailability) return true;
    const slots = dayAvailability[courtId] ?? [];
    if (slots.length === 0) return true;
    if (isSameSchedule && selectedTime) return !slots.includes(selectedTime);
    return false;
  }

  // Staggered schedules (full mode): auto-pick the first court with
  // availability so the time-slot list can render — it is scoped to one court.
  useEffect(() => {
    if (shortcut || isSameSchedule || !dayAvailability) return;
    if (
      selectedCourtId &&
      (dayAvailability[selectedCourtId]?.length ?? 0) > 0
    ) {
      return;
    }
    const first = sportCourts.find(
      (c) => (dayAvailability[c.id]?.length ?? 0) > 0,
    );
    if (first) setSelectedCourtId(first.id);
  }, [shortcut, isSameSchedule, dayAvailability, sportCourts, selectedCourtId]);

  const selectedCourt = useMemo<Court | undefined>(
    () => availQuery.data?.courts.find((c) => c.id === selectedCourtId),
    [availQuery.data, selectedCourtId],
  );
  const selectedSport = useMemo<Sport | undefined>(
    () => availQuery.data?.sports.find((s) => s.id === selectedSportId),
    [availQuery.data, selectedSportId],
  );

  // Coaches teaching the selected sport (with price) for the optional coach
  // picker on the summary step. Empty unless coaching is enabled.
  const coachOptions = useMemo<
    { id: string; name: string; price: string }[]
  >(() => {
    if (!availQuery.data?.features.allowCoaching || !selectedSportId) return [];
    return (availQuery.data.coaches ?? [])
      .map((c) => {
        const sp = c.sports.find((s) => s.sportId === selectedSportId);
        return sp
          ? { id: c.id, name: c.name, price: sp.pricePerSession }
          : null;
      })
      .filter(
        (x): x is { id: string; name: string; price: string } => x !== null,
      );
  }, [availQuery.data, selectedSportId]);

  // Drop a chosen coach if they don't teach the (newly) selected sport.
  useEffect(() => {
    if (
      selectedCoachId &&
      !coachOptions.some((c) => c.id === selectedCoachId)
    ) {
      setSelectedCoachId("");
    }
  }, [coachOptions, selectedCoachId]);

  // Roster collection mode — club-level toggle + selected sport decide it.
  // "format" = singles/doubles radio (tennis & padel); "freeform" = +/- list.
  const rosterMode: "hidden" | "format" | "freeform" = useMemo(() => {
    const require = availQuery.data?.features.requirePlayerRoster ?? false;
    if (!require || !selectedSport) return "hidden";
    if (selectedSport.name === "TENNIS" || selectedSport.name === "PADEL")
      return "format";
    return "freeform";
  }, [availQuery.data?.features.requirePlayerRoster, selectedSport]);

  // Keep the roster length aligned to the current mode + format. Sport
  // changes and singles/doubles flips both route through here. Existing
  // values are preserved across resizes so flipping back keeps names.
  useEffect(() => {
    setRoster((prev) => {
      const empty: RosterEntry = { name: "", playerUserId: null };
      if (rosterMode === "hidden") return prev.length === 0 ? prev : [];
      if (rosterMode === "freeform") {
        if (prev.length >= 1) return prev;
        return [empty];
      }
      const target = rosterFormat === "singles" ? 1 : 3;
      if (prev.length === target) return prev;
      const next = prev.slice(0, target);
      while (next.length < target) next.push({ ...empty });
      return next;
    });
  }, [rosterMode, rosterFormat]);

  const formattedDate = (d: string) => {
    if (!d) return "";
    const e = dateStrToEntry(d);
    return `${t(`days.long.${e.dayKey}`)}, ${e.date}. ${t(`months.${e.monthKey}`)}`;
  };

  const isStep1Complete =
    selectedSportId && selectedDate && selectedTime && selectedCourtId;

  async function submitBooking(override = false) {
    if (!selectedSportId || !selectedDate || !selectedTime || !selectedCourtId)
      return;
    setIsSubmitting(true);
    setErrorCode(null);
    setFormAlertCode(null);
    if (!override) setQuotaPrompt(null);
    try {
      // Coached sessions are always single bookings — recurrence isn't offered
      // for them (and the bulk endpoint takes no coach).
      if (isRecurring && !selectedCoachId) {
        const bulkParsed = bulkBookingSchema.safeParse({
          courtId: selectedCourtId,
          sportId: selectedSportId,
          startTime: selectedTime,
          playerName: playerName.trim(),
          playerPhone: playerPhone.trim() || null,
          playerEmail: playerEmail.trim() || null,
          comment: comment.trim() || null,
          recurrence:
            recurrence.frequency === "DAILY"
              ? {
                  frequency: "DAILY",
                  startDate: selectedDate,
                  untilDate: recurrence.untilDate,
                }
              : {
                  frequency: "WEEKLY",
                  startDate: selectedDate,
                  untilDate: recurrence.untilDate,
                  weekdays: recurrence.weekdays,
                },
        });
        if (!bulkParsed.success) {
          setFormAlertCode(
            bulkParsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
          );
          return;
        }
        const result = await apiPost<BulkBookingResponse>(
          `/api/admin/clubs/${clubId}/bookings/bulk`,
          bulkParsed.data,
        );
        setBulkResult(result);
        setStep("bulk-result");
        onCreated?.();
        return;
      }

      const bookingParsed = adminCreateBookingSchema.safeParse({
        courtId: selectedCourtId,
        sportId: selectedSportId,
        date: selectedDate,
        startTime: selectedTime,
        playerName: playerName.trim(),
        playerPhone: playerPhone.trim() || null,
        playerEmail: playerEmail.trim() || null,
        comment: comment.trim() || null,
        ...(roster.length > 0
          ? {
              players: roster.map((entry) => ({
                name: entry.name.trim(),
                playerUserId: entry.playerUserId,
              })),
            }
          : {}),
        ...(selectedCoachId ? { coachId: selectedCoachId } : {}),
        ...(override ? { override: true } : {}),
      });
      if (!bookingParsed.success) {
        setFormAlertCode(
          bookingParsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
        );
        return;
      }
      const result = await apiPost<BookingResponse>(
        `/api/admin/clubs/${clubId}/bookings`,
        bookingParsed.data,
      );
      setBookingResult(result);
      setStep("success");
      setQuotaPrompt(null);
      onCreated?.();
    } catch (err: unknown) {
      const code = getApiErrorCode(err);
      const status =
        err && typeof err === "object" && "status" in err
          ? (err as { status?: number }).status
          : undefined;
      // Quota violations come back as 409 with one of the QuotaViolationCode
      // codes. Stay on the summary step and surface the override-confirm
      // modal so the admin can decide rather than route to ErrorStep.
      const quotaCodes: QuotaViolationCode[] = [
        "DAILY_LIMIT_EXCEEDED",
        "CREATION_DAY_LIMIT_EXCEEDED",
        "CONSECUTIVE_DAY_NOT_ALLOWED",
        "PRIME_TIME_QUOTA_EXCEEDED",
      ];
      if (
        !isRecurring &&
        status === 409 &&
        quotaCodes.includes(code as QuotaViolationCode)
      ) {
        // apiPost throws `{ status, ...body }` flat — pull both fields off
        // the rejection object directly.
        const errObj = err as {
          violations?: QuotaViolationItem[];
          canOverride?: boolean;
        };
        setQuotaPrompt({
          violations: errObj.violations ?? [
            { code: code as QuotaViolationCode },
          ],
          canOverride: Boolean(errObj.canOverride),
        });
      } else if (COACH_ERROR_CODES.has(code)) {
        // Coach unavailable / taken / mismatch — surface inline so the admin
        // can pick another coach or slot. (COACH_TAKEN is a 409 too, so this
        // must come before the generic conflict branch below.)
        setFormAlertCode(code);
      } else if (!isRecurring && (code === "SLOT_TAKEN" || status === 409)) {
        const alts =
          err && typeof err === "object" && "alternatives" in err
            ? ((err as { alternatives?: Alternative[] }).alternatives ?? [])
            : [];
        setAlternatives(alts);
        setStep("conflict");
        onCreated?.(); // refresh outside data even on conflict
      } else if (FIELD_ERROR_CODES.has(code)) {
        setFormAlertCode(code);
      } else {
        setErrorCode(code);
        setStep("error");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function selectAlternative(time: string, courtId: string) {
    setSelectedTime(time);
    setSelectedCourtId(courtId);
    setStep("summary");
    setAlternatives([]);
    setFormAlertCode(null);
    void availQuery.refetch();
  }

  function backToSelection() {
    setStep(shortcut ? "select-court" : "select");
    setAlternatives([]);
    setErrorCode(null);
    setFormAlertCode(null);
    setBookingResult(null);
    setSelectedCourtId("");
    setSelectedTime(shortcut?.time ?? "");
    void availQuery.refetch();
  }

  function makeAnother() {
    setStep("select");
    setSelectedTime("");
    setSelectedCourtId("");
    setSelectedDate("");
    setPlayerName("");
    setPlayerPhone("");
    setPlayerEmail("");
    setComment("");
    setBookingResult(null);
    setBulkResult(null);
    setIsRecurring(false);
    setRecurrence(defaultRecurrence(""));
    void availQuery.refetch();
  }

  function close() {
    onOpenChange(false);
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="md:max-w-md md:max-h-[90vh] md:overflow-y-auto">
        {availQuery.isLoading ? (
          <DialogLoading />
        ) : !availQuery.data ? (
          <div className="py-10 text-center">
            <p className="text-sm text-neutral-500">
              {t("errors.api.INTERNAL_ERROR")}
            </p>
          </div>
        ) : step === "select-court" && shortcut ? (
          <SelectCourtStep
            availableCourts={shortcutCourts}
            sports={shortcutSports}
            isFree={isFree}
            selectedSportId={selectedSportId}
            onSelectSport={setSelectedSportId}
            date={shortcut.date}
            time={shortcut.time}
            formattedDate={formattedDate(shortcut.date)}
            onSelect={(courtId) => {
              setSelectedCourtId(courtId);
              setStep("summary");
            }}
            onClose={close}
          />
        ) : step === "select" ? (
          <SelectStep
            sports={availQuery.data.sports}
            dates={dates}
            sportCourts={sportCourts}
            selectedSportId={selectedSportId}
            onSelectSport={(id) => {
              setSelectedSportId(id);
              setSelectedTime("");
              setSelectedCourtId("");
            }}
            selectedDate={selectedDate}
            onSelectDate={(d) => {
              setSelectedDate(d);
              setSelectedTime("");
              setSelectedCourtId("");
            }}
            selectedTime={selectedTime}
            onSelectTime={(t) => {
              setSelectedTime(t);
              if (selectedCourtId && isCourtDisabledForTime(selectedCourtId)) {
                setSelectedCourtId("");
              }
            }}
            selectedCourtId={selectedCourtId}
            onSelectCourt={(id) => {
              setSelectedCourtId(id);
              if (!isSameSchedule) setSelectedTime("");
            }}
            visibleTimeSlots={visibleTimeSlots}
            isCourtDisabledForTime={isCourtDisabledForTime}
            isSameSchedule={isSameSchedule}
            hasAnySlot={hasAnySlot}
            onContinue={() => setStep("summary")}
            isStep1Complete={Boolean(isStep1Complete)}
          />
        ) : step === "summary" && selectedCourt && selectedSport ? (
          <SummaryStep
            date={selectedDate}
            formattedDate={formattedDate(selectedDate)}
            time={selectedTime}
            court={selectedCourt}
            sport={selectedSport}
            isFree={isFree}
            coachOptions={coachOptions}
            selectedCoachId={selectedCoachId}
            onSelectCoach={(id) => {
              setSelectedCoachId(id);
              if (id) setIsRecurring(false);
            }}
            playerName={playerName}
            onPlayerNameChange={setPlayerName}
            playerPhone={playerPhone}
            onPlayerPhoneChange={setPlayerPhone}
            playerEmail={playerEmail}
            onPlayerEmailChange={setPlayerEmail}
            phoneEnabled={availQuery.data.features.collectPhone}
            phoneRequired={availQuery.data.features.requirePhone}
            phoneWithCountryCode={
              availQuery.data.features.sendBookingConfirmationSms
            }
            emailEnabled={availQuery.data.features.collectEmail}
            emailRequired={availQuery.data.features.requireEmail}
            comment={comment}
            onCommentChange={setComment}
            roster={roster}
            onRosterChange={setRoster}
            rosterMode={rosterMode}
            rosterFormat={rosterFormat}
            onRosterFormatChange={setRosterFormat}
            // Member-search picker only renders when the club requires login
            // (other clubs don't have a member directory to scope against).
            enableMemberPicker={availQuery.data.features.requireLogin}
            clubId={clubId}
            onBack={backToSelection}
            onSubmit={() => submitBooking(false)}
            isSubmitting={isSubmitting}
            formAlertCode={formAlertCode}
            isRecurring={isRecurring}
            onRecurringChange={setIsRecurring}
            recurrence={recurrence}
            onRecurrenceChange={setRecurrence}
          />
        ) : step === "bulk-result" && bulkResult ? (
          <BulkResultStep
            result={bulkResult}
            onMakeAnother={makeAnother}
            onClose={close}
          />
        ) : step === "success" && bookingResult ? (
          <SuccessStep
            booking={bookingResult}
            formattedDate={formattedDate(bookingResult.date)}
            playerName={playerName}
            playerPhone={playerPhone}
            isFree={isFree}
            onMakeAnother={makeAnother}
            onClose={close}
          />
        ) : step === "conflict" ? (
          <ConflictStep
            requestedDate={selectedDate}
            requestedTime={selectedTime}
            requestedCourtName={selectedCourt?.name ?? ""}
            formattedDate={formattedDate(selectedDate)}
            alternatives={alternatives}
            allCourts={availQuery.data.courts}
            isFree={isFree}
            onSelectAlternative={selectAlternative}
            onBack={backToSelection}
          />
        ) : step === "error" ? (
          <ErrorStep
            code={errorCode}
            onRetry={() => submitBooking(false)}
            onBack={backToSelection}
          />
        ) : null}

        {quotaPrompt && (
          <QuotaOverridePrompt
            prompt={quotaPrompt}
            isSubmitting={isSubmitting}
            onCancel={() => setQuotaPrompt(null)}
            onConfirm={() => void submitBooking(true)}
          />
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

// Inline modal shown when a booking attempt is blocked by a quota rule. We
// stay inside the same dialog (not a separate Dialog) so the user keeps the
// form state intact in case they cancel the override.
function QuotaOverridePrompt({
  prompt,
  isSubmitting,
  onCancel,
  onConfirm,
}: {
  prompt: { violations: QuotaViolationItem[]; canOverride: boolean };
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/95 backdrop-blur-sm rounded-lg p-6">
      <div className="max-w-md w-full space-y-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="space-y-1">
            <h3 className="font-heading font-semibold text-neutral-800">
              {t("bookings.quotaOverride.title")}
            </h3>
            <p className="text-sm text-neutral-600">
              {t("bookings.quotaOverride.description")}
            </p>
          </div>
        </div>
        <ul className="space-y-1.5 pl-1">
          {prompt.violations.map((v, i) => (
            <li
              key={`${v.code}-${i}`}
              className="text-sm text-neutral-700 flex items-start gap-2"
            >
              <span className="text-amber-600">•</span>
              <span>{formatViolation(v, t)}</span>
            </li>
          ))}
        </ul>
        {!prompt.canOverride && (
          <Alert>
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              {t("bookings.quotaOverride.noPermission")}
            </AlertDescription>
          </Alert>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            {t("common.cancel")}
          </Button>
          {prompt.canOverride && (
            <Button
              onClick={onConfirm}
              disabled={isSubmitting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("bookings.quotaOverride.confirm")
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function formatViolation(
  v: QuotaViolationItem,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  let body: string;
  switch (v.code) {
    case "DAILY_LIMIT_EXCEEDED":
      body = t("bookings.quotaOverride.violations.daily", {
        limit: v.limit ?? 0,
      });
      break;
    case "CREATION_DAY_LIMIT_EXCEEDED":
      body = t("bookings.quotaOverride.violations.creationDay", {
        limit: v.limit ?? 0,
      });
      break;
    case "CONSECUTIVE_DAY_NOT_ALLOWED":
      body = t("bookings.quotaOverride.violations.consecutive", {
        date: v.conflictDate ?? "",
      });
      break;
    case "PRIME_TIME_QUOTA_EXCEEDED":
      body = t("bookings.quotaOverride.violations.primeTime", {
        limit: v.limit ?? 0,
      });
      break;
  }
  return v.playerName ? `${v.playerName}: ${body}` : body;
}

// ─── Sub-components ───────────────────────────────────────────────────────

function DialogLoading() {
  return (
    <div className="space-y-4 py-4">
      <Skeleton className="h-6 w-40" />
      <Skeleton className="h-12 w-full rounded-full" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-12 w-full rounded-full" />
    </div>
  );
}

function SelectCourtStep({
  availableCourts,
  sports,
  selectedSportId,
  onSelectSport,
  date,
  time,
  formattedDate,
  isFree,
  onSelect,
  onClose,
}: {
  availableCourts: Court[];
  sports: AvailabilitySport[];
  selectedSportId: string;
  onSelectSport: (id: string) => void;
  date: string;
  time: string;
  formattedDate: string;
  isFree: boolean;
  onSelect: (courtId: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <DialogHeader>
        <DialogTitle>{t("bookings.form.selectCourtTitle")}</DialogTitle>
        <p className="text-sm text-neutral-500 mt-1">
          {formattedDate} • {time}
        </p>
      </DialogHeader>

      {sports.length > 1 && (
        <div className="space-y-2 mt-2">
          <p className="text-sm font-medium text-neutral-700">
            {t("bookings.form.sport")}
          </p>
          <ToggleGroup
            type="single"
            value={selectedSportId}
            onValueChange={(v: string) => v && onSelectSport(v)}
            className="w-full gap-2"
          >
            {sports.map((s) => (
              <ToggleGroupItem
                key={s.sportId}
                value={s.sportId}
                className={`flex-1 ${TOGGLE_CLS}`}
              >
                {t(`sports.${s.sportName}`)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}

      <div className="space-y-2 mt-3">
        {availableCourts.length === 0 ? (
          <div className="text-center py-8 space-y-3">
            <AlertCircle className="w-10 h-10 text-amber-500 mx-auto" />
            <p className="text-sm text-neutral-500">
              {t("bookings.form.noCourtsAvailable")}
            </p>
            <Button
              onClick={onClose}
              variant="outline"
              className="rounded-full"
            >
              {t("common.cancel")}
            </Button>
          </div>
        ) : (
          availableCourts.map((c) => (
            <button
              key={c.id}
              onClick={() => onSelect(c.id)}
              className="w-full p-4 border-2 border-neutral-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all text-left cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-neutral-800">
                    {formatCourtLabel(c.name, t("bookings.form.court"))}
                  </p>
                  <p className="text-sm text-neutral-500">
                    {c.isIndoor
                      ? t("bookings.form.indoor")
                      : t("bookings.form.outdoor")}
                    {!isFree && (
                      <>
                        {" "}
                        ·{" "}
                        {formatRSD(
                          c.pricePerSession,
                          t("bookings.details.free"),
                        )}
                      </>
                    )}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-primary-500" />
              </div>
            </button>
          ))
        )}
      </div>
    </>
  );
}

function SelectStep({
  sports,
  dates,
  sportCourts,
  selectedSportId,
  onSelectSport,
  selectedDate,
  onSelectDate,
  selectedTime,
  onSelectTime,
  selectedCourtId,
  onSelectCourt,
  visibleTimeSlots,
  isCourtDisabledForTime,
  isSameSchedule,
  hasAnySlot,
  onContinue,
  isStep1Complete,
}: {
  sports: Sport[];
  dates: DateEntry[];
  sportCourts: Court[];
  selectedSportId: string;
  onSelectSport: (id: string) => void;
  selectedDate: string;
  onSelectDate: (d: string) => void;
  selectedTime: string;
  onSelectTime: (t: string) => void;
  selectedCourtId: string;
  onSelectCourt: (id: string) => void;
  visibleTimeSlots: string[];
  isCourtDisabledForTime: (id: string) => boolean;
  isSameSchedule: boolean;
  hasAnySlot: boolean;
  onContinue: () => void;
  isStep1Complete: boolean;
}) {
  const { t } = useTranslation();

  const courtSection = (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-neutral-700">
        {t("bookings.form.court")}
      </Label>
      <ToggleGroup
        type="single"
        value={selectedCourtId}
        onValueChange={(v: string) => {
          if (!v) return;
          if (isCourtDisabledForTime(v)) return;
          onSelectCourt(v);
        }}
        className="w-full grid grid-cols-3 gap-2"
      >
        {sportCourts.map((court) => (
          <ToggleGroupItem
            key={court.id}
            value={court.id}
            disabled={isCourtDisabledForTime(court.id)}
            className={TOGGLE_CLS}
          >
            {formatCourtLabel(court.name, t("bookings.form.court"))}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t("bookings.form.newBooking")}</DialogTitle>
      </DialogHeader>

      <div className="space-y-5 mt-2 min-w-0">
        {sports.length > 1 && (
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-neutral-700">
              {t("bookings.form.sport")}
            </Label>
            <ToggleGroup
              type="single"
              value={selectedSportId}
              onValueChange={(v: string) => v && onSelectSport(v)}
              className="w-full gap-2"
            >
              {sports.map((s) => (
                <ToggleGroupItem
                  key={s.id}
                  value={s.id}
                  className={`flex-1 ${TOGGLE_CLS}`}
                >
                  {t(`sports.${s.name}`)}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-sm font-semibold text-neutral-700">
            {t("bookings.form.date")}
          </Label>
          <DateSlider
            dates={dates}
            selectedDate={selectedDate}
            onSelectDate={onSelectDate}
          />
        </div>

        {/* Staggered schedules: court is picked before time. */}
        {selectedDate &&
          hasAnySlot &&
          !isSameSchedule &&
          sportCourts.length > 0 &&
          courtSection}

        {selectedDate &&
          hasAnySlot &&
          (isSameSchedule || selectedCourtId) &&
          visibleTimeSlots.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-neutral-700">
                {t("bookings.form.time")}
              </Label>
              <ToggleGroup
                type="single"
                value={selectedTime}
                onValueChange={(v: string) => v && onSelectTime(v)}
                className="w-full grid grid-cols-3 gap-2"
              >
                {visibleTimeSlots.map((time) => (
                  <ToggleGroupItem
                    key={time}
                    value={time}
                    className={TOGGLE_CLS}
                  >
                    {time}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
          )}

        {selectedDate && !hasAnySlot && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
            {t("bookings.form.noCourtsAvailable")}
          </div>
        )}

        {/* Same schedule: court is picked after time. */}
        {selectedDate &&
          hasAnySlot &&
          isSameSchedule &&
          sportCourts.length > 0 &&
          selectedTime &&
          courtSection}

        <Button
          onClick={onContinue}
          disabled={!isStep1Complete}
          className="w-full rounded-full bg-primary-600 hover:bg-primary-700 text-white py-3 h-auto disabled:opacity-50"
        >
          {t("bookings.form.continue")}
        </Button>
      </div>
    </>
  );
}

function SummaryStep({
  date,
  formattedDate,
  time,
  court,
  sport,
  coachOptions,
  selectedCoachId,
  onSelectCoach,
  playerName,
  onPlayerNameChange,
  playerPhone,
  onPlayerPhoneChange,
  playerEmail,
  onPlayerEmailChange,
  phoneEnabled,
  phoneRequired,
  phoneWithCountryCode,
  emailEnabled,
  emailRequired,
  comment,
  onCommentChange,
  roster,
  onRosterChange,
  rosterMode,
  rosterFormat,
  onRosterFormatChange,
  enableMemberPicker,
  clubId,
  isFree,
  onBack,
  onSubmit,
  isSubmitting,
  formAlertCode,
  isRecurring,
  onRecurringChange,
  recurrence,
  onRecurrenceChange,
}: {
  date: string;
  formattedDate: string;
  time: string;
  court: Court;
  sport: Sport;
  coachOptions: { id: string; name: string; price: string }[];
  selectedCoachId: string;
  onSelectCoach: (id: string) => void;
  playerName: string;
  onPlayerNameChange: (v: string) => void;
  playerPhone: string;
  onPlayerPhoneChange: (v: string) => void;
  playerEmail: string;
  onPlayerEmailChange: (v: string) => void;
  phoneEnabled: boolean;
  phoneRequired: boolean;
  // When the club doesn't send booking-confirmation SMS we hide the country
  // selector — local-format input is enough on the admin booking form.
  phoneWithCountryCode: boolean;
  emailEnabled: boolean;
  emailRequired: boolean;
  comment: string;
  onCommentChange: (v: string) => void;
  roster: RosterEntry[];
  onRosterChange: (next: RosterEntry[]) => void;
  rosterMode: "hidden" | "format" | "freeform";
  rosterFormat: "singles" | "doubles";
  onRosterFormatChange: (format: "singles" | "doubles") => void;
  enableMemberPicker: boolean;
  clubId: string;
  isFree: boolean;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  formAlertCode?: string | null;
  isRecurring: boolean;
  onRecurringChange: (v: boolean) => void;
  recurrence: RecurrenceValue;
  onRecurrenceChange: (v: RecurrenceValue) => void;
}) {
  const { t } = useTranslation();

  // A coached session adds the coach's fee on top of the court price.
  const selectedCoach =
    coachOptions.find((c) => c.id === selectedCoachId) ?? null;
  const coachPrice = selectedCoach?.price ?? null;

  const phoneTrimmed = playerPhone.trim();
  const phoneOk = !phoneEnabled
    ? true
    : !phoneRequired || phoneTrimmed.length >= 4;

  const emailTrimmed = playerEmail.trim();
  const emailValidFormat =
    !emailTrimmed || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);
  const emailMissing = emailEnabled && emailRequired && !emailTrimmed;
  const emailOk = !emailEnabled || (!emailMissing && emailValidFormat);

  const recurrenceOk =
    !isRecurring ||
    (recurrence.untilDate >= date &&
      (recurrence.frequency !== "WEEKLY" || recurrence.weekdays.length > 0));

  // Every roster slot must be filled. Hidden mode auto-passes; freeform must
  // keep at least one row so the requirement isn't bypassed by deleting all.
  const rosterOk =
    rosterMode === "hidden"
      ? true
      : roster.length > 0 &&
        roster.every((entry) => entry.name.trim().length > 0);

  const canSubmit =
    playerName.trim().length >= 2 &&
    phoneOk &&
    emailOk &&
    recurrenceOk &&
    rosterOk &&
    !isSubmitting;

  return (
    <>
      <button
        onClick={onBack}
        disabled={isSubmitting}
        className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 transition-colors cursor-pointer disabled:opacity-50"
      >
        <ChevronLeft className="w-4 h-4" />
        {t("bookings.form.back")}
      </button>

      <DialogHeader>
        <DialogTitle>{t("bookings.form.summary")}</DialogTitle>
      </DialogHeader>

      <div className="space-y-3 pb-3 border-b border-neutral-200">
        <div className="flex items-center gap-3 text-sm text-neutral-700">
          <CalendarIcon className="w-4 h-4 text-primary-500" />
          <span>{formattedDate}</span>
        </div>
        <div className="flex items-center gap-3 text-sm text-neutral-700">
          <Clock className="w-4 h-4 text-primary-500" />
          <span>
            {time} · {durationLabel(sport.sessionDurationMinutes)}
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm text-neutral-700">
          <MapPin className="w-4 h-4 text-primary-500" />
          <span>
            {formatCourtLabel(court.name, t("bookings.form.court"))}{" "}
            <span className="text-neutral-400">
              · {t(`sports.${sport.name}`)}
              {court.isIndoor ? ` · ${t("bookings.form.indoor")}` : ""}
            </span>
          </span>
        </div>
      </div>

      {coachOptions.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-neutral-700">
            {t("bookings.form.coach")}
          </Label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onSelectCoach("")}
              disabled={isSubmitting}
              className={`px-3 py-2 rounded-full text-sm font-medium border transition-colors cursor-pointer disabled:opacity-50 ${
                selectedCoachId === ""
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300"
              }`}
            >
              {t("bookings.form.coachNone")}
            </button>
            {coachOptions.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onSelectCoach(c.id)}
                disabled={isSubmitting}
                className={`px-3 py-2 rounded-full text-sm font-medium border transition-colors cursor-pointer disabled:opacity-50 ${
                  selectedCoachId === c.id
                    ? "border-primary-500 bg-primary-50 text-primary-700"
                    : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300"
                }`}
              >
                {c.name}
                {!isFree ? ` · ${formatRSD(c.price, "")}` : ""}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <h2 className="font-heading font-semibold text-neutral-800">
          {t("bookings.form.guestInfo")}
        </h2>
        {formAlertCode && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t(`errors.api.${formAlertCode}`)}
            </AlertDescription>
          </Alert>
        )}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="b-name" className="text-sm text-neutral-600">
              {t("bookings.form.name")} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="b-name"
              value={playerName}
              onChange={(e) => onPlayerNameChange(e.target.value)}
              placeholder={t("bookings.form.namePlaceholder")}
              disabled={isSubmitting}
            />
          </div>
          {phoneEnabled && (
            <div className="space-y-1.5">
              <Label htmlFor="b-phone" className="text-sm text-neutral-600">
                {t("bookings.form.phone")}{" "}
                {phoneRequired && <span className="text-red-500">*</span>}
              </Label>
              <PhoneInput
                id="b-phone"
                value={playerPhone}
                onChange={onPlayerPhoneChange}
                disabled={isSubmitting}
                showCountrySelector={phoneWithCountryCode}
              />
            </div>
          )}
          {emailEnabled && (
            <div className="space-y-1.5">
              <Label htmlFor="b-email" className="text-sm text-neutral-600">
                {t("bookings.form.email")}{" "}
                {emailRequired && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="b-email"
                type="email"
                value={playerEmail}
                onChange={(e) => onPlayerEmailChange(e.target.value)}
                placeholder={t("bookings.form.emailPlaceholder")}
                disabled={isSubmitting}
                aria-invalid={!emailOk}
                className={!emailOk ? "border-red-400" : ""}
              />
              {emailEnabled && emailTrimmed && !emailValidFormat && (
                <p className="text-xs text-red-500">
                  {t("errors.api.EMAIL_INVALID")}
                </p>
              )}
            </div>
          )}
          {rosterMode !== "hidden" && (
            <div className="space-y-2">
              <div className="space-y-1">
                <Label className="text-sm text-neutral-600">
                  {t("bookings.form.rosterHeading")}{" "}
                  <span className="text-red-500">*</span>
                </Label>
              </div>
              {rosterMode === "format" && (
                <div
                  role="radiogroup"
                  className="grid grid-cols-2 gap-2"
                  aria-label={t("bookings.form.rosterFormatLabel")}
                >
                  {(["singles", "doubles"] as const).map((opt) => {
                    const checked = rosterFormat === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        onClick={() => onRosterFormatChange(opt)}
                        disabled={isSubmitting}
                        className={`px-3 py-2 rounded-full text-sm font-medium border transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${
                          checked
                            ? "border-primary-500 bg-primary-50 text-primary-700"
                            : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300"
                        }`}
                      >
                        {t(
                          opt === "singles"
                            ? "bookings.form.singles"
                            : "bookings.form.doubles",
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="space-y-2">
                {roster.map((entry, i) => {
                  const placeholder = t(
                    rosterMode === "format" && rosterFormat === "doubles"
                      ? i === 0
                        ? "bookings.form.rosterPartnerPlaceholder"
                        : "bookings.form.rosterOpponentPlaceholder"
                      : rosterMode === "format"
                        ? "bookings.form.rosterOpponentPlaceholder"
                        : "bookings.form.rosterPlayerPlaceholder",
                    { index: i + 1 },
                  );
                  return (
                    <div key={i} className="flex items-start gap-2">
                      <div className="flex-1">
                        {enableMemberPicker ? (
                          <AdminMemberSearchInput
                            clubId={clubId}
                            value={entry}
                            placeholder={placeholder}
                            disabled={isSubmitting}
                            onChange={(next) => {
                              const list = roster.slice();
                              list[i] = next;
                              onRosterChange(list);
                            }}
                          />
                        ) : (
                          <Input
                            value={entry.name}
                            onChange={(e) => {
                              const list = roster.slice();
                              list[i] = {
                                name: e.target.value,
                                playerUserId: null,
                              };
                              onRosterChange(list);
                            }}
                            placeholder={placeholder}
                            disabled={isSubmitting}
                          />
                        )}
                      </div>
                      {rosterMode === "freeform" && roster.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const list = roster.slice();
                            list.splice(i, 1);
                            onRosterChange(list);
                          }}
                          disabled={isSubmitting}
                          aria-label={t("bookings.form.rosterRemove")}
                          className="shrink-0 p-2 mt-1 text-neutral-500 hover:text-neutral-700 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {rosterMode === "freeform" && (
                <button
                  type="button"
                  onClick={() =>
                    onRosterChange([
                      ...roster,
                      { name: "", playerUserId: null },
                    ])
                  }
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PlusIcon className="w-4 h-4" />
                  {t("bookings.form.rosterAdd")}
                </button>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="b-comment" className="text-sm text-neutral-600">
              {t("bookings.form.comment")}
            </Label>
            <Textarea
              id="b-comment"
              value={comment}
              onChange={(e) => onCommentChange(e.target.value)}
              placeholder={t("bookings.form.commentPlaceholder")}
              rows={2}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      {/* Recurrence isn't offered for coached sessions (single bookings only). */}
      {!selectedCoachId && (
        <RecurrenceField
          enabled={isRecurring}
          onEnabledChange={onRecurringChange}
          value={recurrence}
          onChange={onRecurrenceChange}
          startDate={date}
          disabled={isSubmitting}
        />
      )}

      {!isFree &&
        (isRecurring ? (
          <div className="flex items-center justify-between pt-2 border-t border-neutral-200">
            <span className="text-sm text-neutral-500">
              {t("bookings.details.price")}
            </span>
            <span className="font-heading font-bold text-neutral-800">
              {t("bookings.form.priceVaries")}
            </span>
          </div>
        ) : coachPrice ? (
          // Coached session: itemize court + coach, then the total.
          <div className="space-y-1 pt-2 border-t border-neutral-200">
            <div className="flex items-center justify-between text-sm text-neutral-500">
              <span>{t("bookings.form.court")}</span>
              <span>{formatRSD(court.pricePerSession, "")}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-neutral-500">
              <span>
                {t("bookings.form.coach")}
                {selectedCoach ? ` · ${selectedCoach.name}` : ""}
              </span>
              <span>+{formatRSD(coachPrice, "")}</span>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-sm text-neutral-500">
                {t("bookings.form.total")}
              </span>
              <span className="font-heading font-bold text-neutral-800">
                {formatRSD(
                  addPrice(court.pricePerSession, coachPrice),
                  t("bookings.details.free"),
                )}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between pt-2 border-t border-neutral-200">
            <span className="text-sm text-neutral-500">
              {t("bookings.details.price")}
            </span>
            <span className="font-heading font-bold text-neutral-800">
              {formatRSD(court.pricePerSession, t("bookings.details.free"))}
            </span>
          </div>
        ))}

      <Button
        onClick={onSubmit}
        disabled={!canSubmit}
        className="w-full rounded-full bg-primary-600 hover:bg-primary-700 text-white py-3 h-auto disabled:opacity-50"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t("bookings.form.creating")}
          </>
        ) : isRecurring ? (
          <>
            <Repeat className="w-4 h-4 mr-2" />
            {t("bookings.form.createSeries")}
          </>
        ) : (
          t("bookings.form.create")
        )}
      </Button>
    </>
  );
}

// Searchable combobox for picking a roster player from the club's APPROVED
// members. Renders a removable chip when linked; falls back to free text in
// the search input otherwise. Debounced 250ms.
function AdminMemberSearchInput({
  clubId,
  value,
  placeholder,
  disabled,
  onChange,
}: {
  clubId: string;
  value: RosterEntry;
  placeholder: string;
  disabled?: boolean;
  onChange: (next: RosterEntry) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<MemberSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Stale-response guard: track the latest query so a slower response can't
  // overwrite the result for a newer one.
  const latestQueryRef = useRef<string>("");

  useEffect(() => {
    if (value.playerUserId) {
      setResults([]);
      return;
    }
    const q = value.name.trim();
    latestQueryRef.current = q;
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      apiGet<{ members: MemberSearchResult[] }>(
        `/api/admin/clubs/${clubId}/members/search?q=${encodeURIComponent(q)}`,
      )
        .then((data) => {
          if (latestQueryRef.current !== q) return;
          setResults(data.members);
        })
        .catch(() => {
          if (latestQueryRef.current !== q) return;
          setResults([]);
        })
        .finally(() => {
          if (latestQueryRef.current !== q) return;
          setLoading(false);
        });
    }, 250);
    return () => clearTimeout(handle);
  }, [value.name, value.playerUserId, clubId]);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  if (value.playerUserId) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
        <span className="text-sm font-medium text-primary-800 truncate">
          {value.name}
        </span>
        <button
          type="button"
          aria-label={t("bookings.form.rosterClear")}
          disabled={disabled}
          onClick={() => onChange({ name: "", playerUserId: null })}
          className="ml-auto shrink-0 text-primary-600 hover:text-primary-800 cursor-pointer disabled:opacity-50"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const query = value.name.trim();
  const showMenu = open && query.length >= 2;

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={value.name}
        placeholder={placeholder || t("bookings.form.rosterSearchPlaceholder")}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setOpen(true);
          onChange({ name: e.target.value, playerUserId: null });
        }}
      />
      {showMenu && (
        <div className="absolute left-0 right-0 top-full mt-1 z-20 max-h-60 overflow-auto rounded-md border border-neutral-200 bg-white shadow-lg">
          {loading && (
            <div className="px-3 py-2 text-xs text-neutral-500">
              {t("bookings.form.rosterSearching")}
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-neutral-500">
              {t("bookings.form.rosterSearchNoMatch")}
            </div>
          )}
          {!loading &&
            results.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  onChange({ name: m.name, playerUserId: m.id });
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 hover:bg-primary-50 cursor-pointer"
              >
                <div className="text-sm font-medium text-neutral-800">
                  {m.name}
                </div>
                <div className="text-xs text-neutral-500 truncate">
                  {m.email}
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function SuccessStep({
  booking,
  formattedDate,
  playerName,
  playerPhone,
  isFree,
  onMakeAnother,
  onClose,
}: {
  booking: BookingResponse;
  formattedDate: string;
  playerName: string;
  playerPhone: string;
  isFree: boolean;
  onMakeAnother: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5 py-2">
      <div className="flex justify-center">
        <CheckCircle2 className="w-16 h-16 text-emerald-500" />
      </div>
      <div className="text-center space-y-1">
        <h2 className="font-heading text-xl font-bold text-neutral-800">
          {t("bookings.form.successTitle")}
        </h2>
        <p className="text-sm text-neutral-500">
          {t("bookings.form.successMessage", { name: playerName })}
        </p>
      </div>

      <div className="bg-neutral-50 rounded-lg p-4 space-y-2">
        <div className="flex items-start gap-3 pb-3 border-b border-neutral-200">
          <Hash className="w-5 h-5 text-primary-500 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-neutral-500">
              {t("bookings.details.ref")}
            </p>
            <p className="font-mono font-semibold text-neutral-800 break-all">
              {booking.bookingRef}
            </p>
          </div>
        </div>
        <Row label={t("bookings.details.player")} value={playerName} />
        {playerPhone ? (
          <Row label={t("bookings.details.playerPhone")} value={playerPhone} />
        ) : null}
        <Row label={t("bookings.details.court")} value={booking.courtName} />
        <Row
          label={t("bookings.details.date")}
          value={`${formattedDate} · ${booking.startTime}`}
        />
        <Row label={t("bookings.details.duration")} value={booking.duration} />
        {!isFree && (
          <Row
            label={t("bookings.details.price")}
            value={formatRSD(booking.price, t("bookings.details.free"))}
          />
        )}
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onMakeAnother}
          className="flex-1 rounded-full"
        >
          {t("bookings.form.newOne")}
        </Button>
        <Button
          onClick={onClose}
          className="flex-1 rounded-full bg-primary-600 hover:bg-primary-700 text-white"
        >
          {t("bookings.form.close")}
        </Button>
      </div>
    </div>
  );
}

function ConflictStep({
  requestedDate,
  requestedTime,
  requestedCourtName,
  formattedDate,
  alternatives,
  allCourts,
  isFree,
  onSelectAlternative,
  onBack,
}: {
  requestedDate: string;
  requestedTime: string;
  requestedCourtName: string;
  formattedDate: string;
  alternatives: Alternative[];
  allCourts: Court[];
  isFree: boolean;
  onSelectAlternative: (time: string, courtId: string) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  void requestedDate;
  return (
    <div className="space-y-5 py-2">
      <div className="flex justify-center">
        <div className="bg-orange-100 rounded-full p-3">
          <AlertCircle className="w-10 h-10 text-orange-600" />
        </div>
      </div>
      <div className="text-center space-y-1">
        <h2 className="font-heading text-xl font-bold text-neutral-800">
          {t("bookings.form.conflictTitle")}
        </h2>
        <p className="text-sm text-neutral-500">
          {t("bookings.form.conflictMessage", {
            court: formatCourtLabel(
              requestedCourtName,
              t("bookings.form.court"),
            ),
            time: requestedTime,
          })}
        </p>
      </div>

      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-1.5">
        <div className="flex items-center gap-2 text-sm text-neutral-700">
          <CalendarIcon className="w-4 h-4 text-orange-600" />
          {formattedDate}
        </div>
        <div className="flex items-center gap-2 text-sm text-neutral-700">
          <Clock className="w-4 h-4 text-orange-600" />
          {requestedTime} ·{" "}
          {formatCourtLabel(requestedCourtName, t("bookings.form.court"))}
        </div>
      </div>

      {alternatives.length > 0 ? (
        <div className="space-y-2">
          <p className="font-heading font-semibold text-sm text-neutral-700">
            {t("bookings.form.alternativeSlots")}
          </p>
          {alternatives.map((alt, i) => {
            const court = allCourts.find((c) => c.id === alt.courtId);
            return (
              <button
                key={i}
                onClick={() => onSelectAlternative(alt.time, alt.courtId)}
                className="w-full bg-white hover:bg-neutral-50 border border-neutral-200 rounded-lg p-3 text-left transition-colors group cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-neutral-800">{alt.time}</p>
                    <p className="text-sm text-neutral-500">
                      {formatCourtLabel(
                        alt.courtName,
                        t("bookings.form.court"),
                      )}
                      {!isFree && court
                        ? ` · ${formatRSD(court.pricePerSession, t("bookings.details.free"))}`
                        : ""}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-primary-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-sm text-neutral-500">
          {t("bookings.form.noAlternatives")}
        </p>
      )}

      <Button
        onClick={onBack}
        className="w-full rounded-full bg-primary-600 hover:bg-primary-700 text-white"
      >
        {t("bookings.form.back")}
      </Button>
    </div>
  );
}

function ErrorStep({
  code,
  onRetry,
  onBack,
}: {
  code: string | null;
  onRetry: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  // Surface the API error code in toast on mount for visibility.
  useEffect(() => {
    if (code) toast.error(t(`errors.api.${code}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div className="space-y-5 py-2">
      <div className="flex justify-center">
        <div className="bg-red-100 rounded-full p-3">
          <XCircle className="w-10 h-10 text-red-600" />
        </div>
      </div>
      <div className="text-center space-y-1">
        <h2 className="font-heading text-xl font-bold text-neutral-800">
          {t("bookings.form.errorTitle")}
        </h2>
        <p className="text-sm text-neutral-500">
          {code ? t(`errors.api.${code}`) : t("bookings.form.errorMessage")}
        </p>
      </div>

      <div className="space-y-2">
        <Button
          onClick={onRetry}
          className="w-full rounded-full bg-primary-600 hover:bg-primary-700 text-white"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {t("bookings.form.tryAgain")}
        </Button>
        <Button
          onClick={onBack}
          variant="outline"
          className="w-full rounded-full"
        >
          {t("bookings.form.back")}
        </Button>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-neutral-500">{label}</span>
      <span className="font-medium text-neutral-800">{value}</span>
    </div>
  );
}

function BulkResultStep({
  result,
  onMakeAnother,
  onClose,
}: {
  result: BulkBookingResponse;
  onMakeAnother: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { created, conflicts } = result;
  const allFailed = created.length === 0;

  function formatRow(date: string) {
    const e = dateStrToEntry(date);
    return `${t(`days.long.${e.dayKey}`)}, ${e.date}. ${t(`months.${e.monthKey}`).slice(0, 3)}`;
  }

  return (
    <div className="space-y-5 py-2">
      <div className="flex justify-center">
        {allFailed ? (
          <div className="bg-red-100 rounded-full p-3">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
        ) : conflicts.length > 0 ? (
          <div className="bg-amber-100 rounded-full p-3">
            <AlertCircle className="w-10 h-10 text-amber-600" />
          </div>
        ) : (
          <CheckCircle2 className="w-14 h-14 text-emerald-500" />
        )}
      </div>

      <div className="text-center space-y-1">
        <h2 className="font-heading text-xl font-bold text-neutral-800">
          {allFailed
            ? t("bookings.form.bulkAllFailedTitle")
            : conflicts.length > 0
              ? t("bookings.form.bulkPartialTitle")
              : t("bookings.form.bulkSuccessTitle")}
        </h2>
        <p className="text-sm text-neutral-500">
          {t("bookings.form.bulkSummary", {
            created: created.length,
            conflicts: conflicts.length,
          })}
        </p>
      </div>

      {created.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1.5 max-h-60 overflow-y-auto">
          <p className="font-heading font-semibold text-sm text-emerald-800 mb-1">
            {t("bookings.form.bulkCreatedHeading", { count: created.length })}
          </p>
          {created.map((b) => (
            <div
              key={b.id}
              className="flex items-center justify-between text-sm text-neutral-700"
            >
              <span>{formatRow(b.date)}</span>
              <span className="text-neutral-500">{b.startTime}</span>
            </div>
          ))}
        </div>
      )}

      {conflicts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5 max-h-60 overflow-y-auto">
          <p className="font-heading font-semibold text-sm text-amber-800 mb-1">
            {t("bookings.form.bulkConflictsHeading", {
              count: conflicts.length,
            })}
          </p>
          {conflicts.map((c, i) => (
            <div
              key={`${c.date}-${i}`}
              className="flex items-center justify-between gap-2 text-sm text-neutral-700"
            >
              <span>{formatRow(c.date)}</span>
              <span className="text-xs text-neutral-500">
                {c.startTime} ·{" "}
                {t(`bookings.form.bulkConflictReason.${c.reason}`)}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onMakeAnother}
          className="flex-1 rounded-full"
        >
          {t("bookings.form.newOne")}
        </Button>
        <Button
          onClick={onClose}
          className="flex-1 rounded-full bg-primary-600 hover:bg-primary-700 text-white"
        >
          {t("bookings.form.close")}
        </Button>
      </div>
    </div>
  );
}
